import { useEffect, useRef, useState } from 'react';

/**
 * Hook for managing Screen Wake Lock API during recording sessions
 * Keeps screen awake on mobile devices and handles lifecycle events
 *
 * @param {boolean} isActive - Whether wake lock should be active (e.g., during recording)
 * @returns {object} { isSupported, isLocked, error }
 */
export const useWakeLock = (isActive) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [error, setError] = useState(null);
  const wakeLockRef = useRef(null);
  const isActiveRef = useRef(isActive);

  // Update ref when isActive changes
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Check if Wake Lock API is supported
  useEffect(() => {
    const supported = 'wakeLock' in navigator;
    setIsSupported(supported);

    if (!supported) {
      console.log('Screen Wake Lock API not supported in this browser');
    }
  }, []);

  // Request wake lock
  const requestWakeLock = async () => {
    if (!isSupported || wakeLockRef.current !== null) {
      return;
    }

    try {
      const wakeLock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = wakeLock;
      setIsLocked(true);
      setError(null);
      console.log('Screen Wake Lock acquired');

      // Listen for wake lock release
      wakeLock.addEventListener('release', () => {
        console.log('Screen Wake Lock released');
        wakeLockRef.current = null;
        setIsLocked(false);
      });
    } catch (err) {
      console.error('Failed to acquire Screen Wake Lock:', err);
      setError(err.message);
      wakeLockRef.current = null;
      setIsLocked(false);
    }
  };

  // Release wake lock
  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        setIsLocked(false);
        console.log('Screen Wake Lock manually released');
      } catch (err) {
        console.error('Failed to release Screen Wake Lock:', err);
      }
    }
  };

  // Handle visibility change (tab backgrounded/foregrounded)
  useEffect(() => {
    if (!isSupported) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isActiveRef.current) {
        // Tab became visible again and recording is still active - re-acquire lock
        console.log('Tab visible again, re-acquiring wake lock');
        await requestWakeLock();
      } else if (document.visibilityState === 'hidden') {
        // Tab backgrounded - wake lock is automatically released by browser
        console.log('Tab hidden, wake lock will be released by browser');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSupported]);

  // Request or release wake lock based on isActive
  useEffect(() => {
    if (!isSupported) return;

    if (isActive) {
      // Only request if document is visible
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    } else {
      releaseWakeLock();
    }

    // Cleanup on unmount or when isActive changes
    return () => {
      if (!isActive) {
        releaseWakeLock();
      }
    };
  }, [isActive, isSupported]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      releaseWakeLock();
    };
  }, []);

  return {
    isSupported,
    isLocked,
    error
  };
};
